import { DialogCloseError, DialogCloseResult } from 'aurelia-dialog';
import { autoinject, computedFrom, PLATFORM } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { NotesApi } from '~/api/notes-api';
import { BaseDialog, BaseDialogModel } from '~/components/dialog/base-dialog';
import { CustomKeysDialog } from '~/components/dialog/custom-keys-dialog';
import { SimpleDialogType } from '~/components/dialog/simple-dialog';
import { Notification } from '~/components/notification/notification';
import { ShortcutKey } from '~/constants';
import { NotesModel } from '~/models/notes-model';
import { DialogUtils } from '~/utils/dialog-utils';
import { Key } from '~/utils/key';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';

import styles from '~/components/notes/notes-dialog.module.scss';

export interface NotesDialogModel extends BaseDialogModel {
	functionName: string;
	noteTitle?: string;
	recordId: number;
	readOnly?: boolean;
	queryParams?: Map<string, string>;
}

export interface NotesDialogResult {
	/**
	 * Notes where successfully Saved or Deleted.
	 */
	haveNotesChanged: boolean;
}

@autoinject
export class NotesDialog extends BaseDialog<NotesDialogModel> {
	/**
	 * styles is forwarding our styles to the *.html template
	 */
	private styles = styles;

	private noteText: string;
	private recordHasNotes: boolean;

	constructor(
		protected readonly i18n: I18N,
		protected readonly dialogUtils: DialogUtils,
		protected readonly notesApi: NotesApi,
		...rest
	) {
		super(...rest);
		PLATFORM.moduleName('./notes-dialog');
	}

	public activate(model: NotesDialogModel): Promise<NotesDialogModel> {
		return super.activate(model).then(async () => {
			this.model.dialogSize = NotesDialog.DialogSize.MEDIUM;
			const notesModel = await this.notesApi.getNotesData(
				this.model.functionName,
				this.model.recordId,
				this.model.queryParams,
			);
			this.noteText = notesModel.noteText;
			this.recordHasNotes = NotesApi.hasNotes(notesModel);
			return this.model;
		});
	}

	// protected deactivate(result: DialogCloseResult | DialogCloseError) {
	// 	super.deactivate(result);
	// 	result.output = this.model;
	// }

	protected attached() {
		super.attached();

		KeyboardShortcut.subscribe(
			this,
			ShortcutKey.Close,
			this.element.querySelectorAll('[click\\.trigger="closeClicked()"]'),
		);
		KeyboardShortcut.subscribe(
			this,
			ShortcutKey.Save,
			this.element.querySelectorAll('[click\\.trigger="saveClicked()"]'),
		);
		KeyboardShortcut.subscribe(
			this,
			ShortcutKey.Delete,
			this.element.querySelectorAll(
				'[click\\.trigger="showDeleteNotesDialog()"]',
			),
		);

		// Ignore 'ENTER" keydown event so user is able to type in new lines in textarea
		const noteTextAreaElem =
			this.element.querySelector<HTMLTextAreaElement>(
				'.a-notes-dialog__textarea',
			);
		if (noteTextAreaElem) {
			noteTextAreaElem.addEventListener('keydown', (event) => {
				if (event.key === Key.ENTER) {
					event.stopPropagation();
				}
			});
		} else {
			Logger.warn(
				'Unable to add event listener to notes textarea element',
			);
		}
	}

	protected detached() {
		KeyboardShortcut.unsubscribeAll(this);
	}

	private showDeleteNotesDialog(): void {
		if (!this.canDelete) {
			return;
		}
		const dialogModel = this.dialogUtils.getSimpleDialogModel({
			simpleDialogType: SimpleDialogType.WARNING,
			bodyText: this.i18n.tr('slideout.delete-notes-ask', {
				'notes-name': this.model.noteTitle
					? this.model.noteTitle
					: '$t(slideout.notes)',
			}),
		});
		this.dialogUtils
			.showCustomDialog(CustomKeysDialog, dialogModel)
			.whenClosed((value) => {
				if (!value.wasCancelled) {
					this.deleteNotes();
				}
			});
	}

	public getNoteLineCount(noteText: string): number {
		if (noteText == null || noteText === '') {
			return 0;
		}
		return noteText.split(/\n/).length;
	}

	private saveClicked() {
		if (!this.canSave) {
			return;
		}
		this.saveNotes();
	}

	private closeClicked() {
		this.controller.cancel({
			haveNotesChanged: false,
		} as NotesDialogResult);
	}

	private deleteNotes(): void {
		this.notesApi
			.deleteNotesData(
				this.model.functionName,
				this.model.recordId,
				this.model.queryParams,
			)
			.then(() => {
				Notification.success(
					this.i18n.tr('successes.notes-were-deleted-successfully'),
				);
				this.controller.cancel({
					haveNotesChanged: true,
				} as NotesDialogResult);
			})
			.catch((error) => {
				const message = NotesDialog.getMessage(error);
				Notification.error(
					this.i18n.tr('errors.unable-to-delete-notes', { message }),
				);
			});
	}

	private saveNotes(): void {
		const notesModel: NotesModel = {
			noteText: this.noteText,
			inError: false,
		};
		this.notesApi
			.saveNotesData(
				this.model.functionName,
				this.model.recordId,
				notesModel,
				this.model.queryParams,
			)
			.then(() => {
				Notification.success(
					this.i18n.tr('successes.notes-were-saved-successfully'),
				);
				this.controller.ok({
					haveNotesChanged: true,
				} as NotesDialogResult);
			})
			.catch((error) => {
				const message = NotesDialog.getMessage(error);
				Notification.error(
					this.i18n.tr('errors.unable-to-save-notes', { message }),
				);
			});
	}

	private static getMessage(
		error: string | { message?: string; error?: string },
	): string {
		// NOTE: Jake: 2019-11-15
		// This handling should be removed after completing WEBUI-2597 as the fetch
		// code should become more robust.
		let message = '';
		if (error) {
			if (typeof error === 'string') {
				message = error;
			} else if (typeof error === 'object') {
				if (error.message) {
					message = error.message;
				} else if (error.error) {
					message = error.error;
				}
			}
		}
		message = message.trim();
		return message;
	}

	@computedFrom('noteText', 'noteText.length', 'model.readOnly')
	get canSave(): boolean {
		return this.noteText.length > 0 && !this.model.readOnly;
	}

	@computedFrom('recordHasNotes', 'model.readOnly')
	get canDelete(): boolean {
		return this.recordHasNotes && !this.model.readOnly;
	}
}
